'use client';

import { useEffect, useRef, type RefObject } from 'react';

import { trackPreviewView } from '@/lib/analytics';

// Shared IntersectionObserver для трекинга просмотров статей
// Один observer на все карточки вместо создания отдельного на каждую
// Улучшает INP/TBT за счёт уменьшения количества observer-экземпляров
const callbackMap = typeof window !== 'undefined' ? new WeakMap<Element, () => void>() : null;

const sharedObserver =
  typeof window !== 'undefined'
    ? new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const callback = callbackMap?.get(entry.target);
              callback?.();
              sharedObserver.unobserve(entry.target);
            }
          });
        },
        {
          threshold: 0.5, // Trigger when 50% of the card is visible
          rootMargin: '0px',
        },
      )
    : null;

/**
 * Хук для трекинга просмотра статьи при появлении в viewport
 * Использует shared IntersectionObserver для уменьшения TBT
 * @param ref - Ref на элемент карточки
 * @param articleId - ID статьи для аналитики
 */
export function useArticleViewTracker(ref: RefObject<Element | null>, articleId: string) {
  useEffect(() => {
    if (!ref.current || !sharedObserver) return;

    const callback = () => {
      trackPreviewView(articleId);
    };

    callbackMap?.set(ref.current, callback);
    sharedObserver.observe(ref.current);

    return () => {
      if (ref.current) {
        sharedObserver.unobserve(ref.current);
        callbackMap?.delete(ref.current);
      }
    };
  }, [ref, articleId]);
}
